import type { FormItemProps } from 'antd';
import type { Rule } from 'antd/es/form';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type {
  FmFieldDescriptor,
  FmFieldDescriptorBase,
  FmFormActionDescriptor,
  FmFormFieldIntent,
} from './FmForm.types';
import type { FmButtonVariant } from './FmButton';

export const fieldIntentToStatus = (
  intent?: FmFormFieldIntent,
): FormItemProps['validateStatus'] | undefined => {
  if (intent === 'danger') return 'error';
  if (intent === 'warning') return 'warning';
  if (intent === 'success') return 'success';
  return undefined;
};

export const buildRules = (
  descriptor: FmFieldDescriptor,
  requiredMessage: string,
): Rule[] | undefined => {
  const rules = [...(descriptor.validation?.rules ?? [])];
  const shouldRequire = Boolean(descriptor.required || descriptor.validation?.required);
  if (shouldRequire) {
    rules.push({
      required: true,
      message: descriptor.validation?.message ?? requiredMessage,
    });
  }
  return rules.length > 0 ? rules : undefined;
};

export const isLocked = (descriptor: FmFieldDescriptor): boolean =>
  Boolean(descriptor.disabled || descriptor.disabledReason || descriptor.lockedReason);

export const normalizeMiddleSize = (
  size: FmFieldDescriptorBase['size'],
): 'small' | 'middle' | 'large' | undefined => (size === 'medium' ? 'middle' : size);

export const actionVariant = (intent: FmFormActionDescriptor['intent']): FmButtonVariant =>
  intent === 'primary' ? 'primary' : 'default';

/**
 * Option count from which the design system lets AntD virtualize a select popup.
 *
 * AntD v6 (@rc-component/select OptionList) only puts `role="option"`,
 * `aria-selected` and `aria-disabled` on the VISIBLE rows when the list is NOT
 * virtualized. Under virtualization those attributes move to a hidden 0x0
 * mirror listbox that holds just the three options around the active index, so
 * assistive technology and role-based queries can neither see the rows the user
 * sees nor enumerate the full option set.
 *
 * Below this ceiling virtualization buys nothing — the popup renders every row
 * into the DOM anyway — so the design system keeps the semantics on the visible
 * rows. Longer lists stay virtualized, where the mirror is rc-select's intended
 * assistive-technology contract and the DOM saving is real.
 */
export const FM_SELECT_VIRTUALIZATION_MIN_OPTIONS = 40;

export const shouldVirtualizeOptions = (options: readonly unknown[] | undefined): boolean =>
  options !== undefined && options.length >= FM_SELECT_VIRTUALIZATION_MIN_OPTIONS;

/**
 * A date field's `value` may be a `Dayjs` instance or a plain ISO string (so a
 * caller with no other reason to depend on `dayjs` — route-owned code in
 * particular — never has to import it). The field owns the conversion.
 */
export const resolveDateFieldValue = (
  value: Dayjs | string | null | undefined,
): Dayjs | null | undefined => {
  if (typeof value !== 'string') return value;
  return value ? dayjs(value) : null;
};

/** Same ISO-string-or-Dayjs contract as {@link resolveDateFieldValue}, for `defaultValue`. */
export const resolveDateFieldDefaultValue = (
  value: Dayjs | string | undefined,
): Dayjs | undefined => (typeof value === 'string' ? dayjs(value) : value);

export function selectControlClassName(
  controlWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'fill',
): string {
  return `fm-select fm-select--width-${controlWidth ?? 'fill'}`;
}

/**
 * Form initial values with every date field's ISO string read as the `Dayjs`
 * the picker holds, so callers seed a date the same way they pass its
 * `value`/`defaultValue` (an ISO string) and never import `dayjs` themselves.
 */
export function normalizeFormInitialValues(
  initialValues: Record<string, unknown> | undefined,
  sections: ReadonlyArray<{ fieldDescriptors: FmFieldDescriptor[] }>,
): Record<string, unknown> | undefined {
  if (!initialValues) return initialValues;
  const normalized = { ...initialValues };
  for (const section of sections) {
    for (const descriptor of section.fieldDescriptors) {
      const value = normalized[descriptor.name];
      if (descriptor.kind === 'date' && typeof value === 'string') {
        normalized[descriptor.name] = resolveDateFieldValue(value);
      }
    }
  }
  return normalized;
}

